import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';

import defaultStyles from './StaticDataMappings.module.scss';
import * as constants from './constants';
import {
    staticDataMappingsLoaded, fetchDropdownOptions, createNewMapping, deleteMapping, editMapping,
    setGridSorts
} from './actions';
import {
    getEquityCreditRows, getIsdaRows, getCommodityRows, getEquityMappingRows,
    getCommodityLoading, getEquityCreditLoading, getEquityMappingLoading, getIsdaLoading,
    getIsdaMappingDropdownOptions, getCommodityMappingDropdownOptions, getDefaultSort,
    getStandardProductClassRows, getStandardProductClassDropdown, getStandardProductClassLoading,
    getCurrencyMappingRows, getCurrencyMappingLoading, getCurrencyDropdownOptions
} from './selectors';
import CustomGrid from '../customgrid/CustomGrid';
import CustomGridWithModal from '../customgrid/CustomGridWithModal';
import { findGridSort } from '../../common/helpermethods/HelperMethods';

const processCommodityMappingDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const { field, mappedField } = row;
        return { Commodity: field, 'ISDA Commodity': mappedField };
    };
    return rows.map(mapDataKeys);
};

const processIsdaCurveDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const { field, mappedField } = row;
        return { 'FO Curve Name': field, 'ISDA Mapping': mappedField };
    };
    return rows.map(mapDataKeys);
};

const processEquityCreditDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const { type, isin, bucket } = row;
        return { Type: type, ISIN: isin, 'Risk Bucket': bucket };
    };
    return rows.map(mapDataKeys);
};

const processNonIsinDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const { field, mappedField } = row;
        return { 'SEDOL/CUSIP': field, ISIN: mappedField };
    };
    return rows.map(mapDataKeys);
};

const processStandardProductClassForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const { field, mappedField } = row;
        return { 'Data Type': field, 'Standard Product Class': mappedField };
    };
    return rows.map(mapDataKeys);
};

const processCurrencyMappingForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const { field, mappedField } = row;
        return { 'Onshore Currency': field, 'Offshore Mapping': mappedField };
    };
    return rows.map(mapDataKeys);
};

const StaticDataMappings = (props) => {
    const { staticDataMappingsLoaded } = props;

    useEffect(() => {
        staticDataMappingsLoaded();
    }, [staticDataMappingsLoaded]);

    const setGridSorted = (gridId, newSorted) => {
        props.setGridSorts(gridId, newSorted);
    };

    return (
        <div className={ props.styles.staticDataMappingsWrapper }>
            <div className={ props.styles.staticDataMappingsRow }>
                <CustomGrid
                    columns={ constants.equityCreditColumns }
                    csvFilename="Equity_Credit.csv"
                    csvMapping={ processEquityCreditDataForCsvExport }
                    defaultSorted={ findGridSort(props.defaultSort, 'equityCredit') }
                    filterable
                    loading={ props.equityCreditLoading }
                    onSortedChange={ newSorted => setGridSorted('equityCredit', newSorted) }
                    rows={ props.equityCreditRows }
                    styles={ props.styles }
                    pagination
                    title="Equity and Credit Mapping Via ICE"
                />
                <CustomGridWithModal
                    columns={ constants.isdaColumns }
                    csvFilename="Isda_Curve.csv"
                    csvMapping={ processIsdaCurveDataForCsvExport }
                    createAction={ props.createNewMapping('isda') }
                    createMappingMessage="Create new Isda Mapping"
                    defaultSorted={ findGridSort(props.defaultSort, 'isda') }
                    deleteRow={ props.deleteMapping('isda') }
                    dropdownOptions={ props.isdaMappingDropdownOptions }
                    editMapping={ props.editMapping('isda') }
                    editMappingMessage="Edit an existing Isda Mapping"
                    fetchDropdownOptions={ props.fetchDropdownOptions('isda') }
                    filterable
                    loading={ props.isdaLoading }
                    onSortedChange={ newSorted => setGridSorted('isda', newSorted) }
                    optionOneKey="FO Curve Name"
                    optionTwoKey="ISDA Mapping"
                    rows={ props.isdaRows }
                    secondOptionDropdown
                    styles={ props.styles }
                    pagination
                    title="ISDA Sub Curve Mapping"
                />
            </div>

            <div className={ props.styles.staticDataMappingsRow }>
                <CustomGridWithModal
                    columns={ constants.commodityColumns }
                    csvFilename="Commodity_Mapping.csv"
                    csvMapping={ processCommodityMappingDataForCsvExport }
                    createAction={ props.createNewMapping('commodity') }
                    createMappingMessage="Create new Commodity Mapping"
                    defaultSorted={ findGridSort(props.defaultSort, 'commodity') }
                    deleteRow={ props.deleteMapping('commodity') }
                    dropdownOptions={ props.commodityMappingDropdownOptions }
                    editMapping={ props.editMapping('commodity') }
                    editMappingMessage="Edit an existing Commodity Mapping"
                    fetchDropdownOptions={ props.fetchDropdownOptions('commodity') }
                    filterable
                    loading={ props.commodityLoading }
                    onSortedChange={ newSorted => setGridSorted('commodity', newSorted) }
                    optionOneKey="Commodity"
                    optionTwoKey="ISDA Commodity"
                    rows={ props.commodityRows }
                    secondOptionDropdown
                    styles={ props.styles }
                    pagination
                    title="Commodity Mapping"
                />
                <CustomGridWithModal
                    columns={ constants.equityMappingColumns }
                    csvFilename="Equity_Mapping.csv"
                    csvMapping={ processNonIsinDataForCsvExport }
                    createAction={ props.createNewMapping('equity') }
                    createMappingMessage="Create new Equity Mapping"
                    defaultSorted={ findGridSort(props.defaultSort, 'equity') }
                    deleteRow={ props.deleteMapping('equity') }
                    editMapping={ props.editMapping('equity') }
                    editMappingMessage="Edit an existing Equity Mapping"
                    filterable
                    loading={ props.equityMappingLoading }
                    onSortedChange={ newSorted => setGridSorted('equity', newSorted) }
                    optionOneKey="SEDOL/CUSIP"
                    optionTwoKey="ISDA ISIN"
                    rows={ props.equityMappingRows }
                    styles={ props.styles }
                    pagination
                    title="Equity Mapping (non-ISIN)"
                />
            </div>
            <div className={ props.styles.staticDataMappingsRow }>
                <CustomGridWithModal
                    title="Currency Mapping"
                    columns={ constants.currencyMappingColumns }
                    rows={ props.currencyMappingRows }
                    csvFilename="Currency_Mapping.csv"
                    csvMapping={ processCurrencyMappingForCsvExport }
                    createAction={ props.createNewMapping('currency') }
                    createMappingMessage="Create new Currency Mapping"
                    editMapping={ props.editMapping('currency') }
                    editMappingMessage="Edit Curency Mapping"
                    deleteRow={ props.deleteMapping('currency') }
                    loading={ props.currencyMappingLoading }
                    onSortedChange={ newSorted => setGridSorted('currency', newSorted) }
                    defaultSorted={ findGridSort(props.defaultSort, 'currency') }
                    optionOneKey="Onshore Currency"
                    optionTwoKey="Offshore Currency"
                    dropdownOptions={ props.currencyMappingDropdown }
                    fetchDropdownOptions={ props.fetchDropdownOptions('currency') }
                    secondOptionDropdown
                    styles={ props.styles }
                    pagination
                    filterable
                />
                <CustomGridWithModal
                    title="Standard Product Class"
                    columns={ constants.standardProductClassColumns }
                    rows={ props.standardProductClassRows }
                    csvFilename="Standard_Product_Class_Mapping.csv"
                    csvMapping={ processStandardProductClassForCsvExport }
                    editMapping={ props.editMapping('productClass') }
                    editMappingMessage="Edit Standard Product Class"
                    loading={ props.standardProductClassLoading }
                    onSortedChange={ newSorted => setGridSorted('productClass', newSorted) }
                    defaultSorted={ findGridSort(props.defaultSort, 'productClass') }
                    optionOneKey="Data Type"
                    optionTwoKey="Standard Product Class"
                    secondOptionDropdown
                    firstOptionReadOnly
                    dropdownOptions={ props.standardProductClassDropdown }
                    fetchDropdownOptions={ props.fetchDropdownOptions('productClass') }
                    styles={ props.styles }
                    pagination
                    filterable
                    hideCreateModal
                />
            </div>
        </div>
    );
};

StaticDataMappings.propTypes = {
    createNewMapping: PropTypes.func.isRequired,
    commodityLoading: PropTypes.bool,
    commodityMappingDropdownOptions: PropTypes.arrayOf(PropTypes.string),
    commodityRows: PropTypes.arrayOf(PropTypes.shape({})),
    deleteMapping: PropTypes.func.isRequired,
    defaultSort: PropTypes.arrayOf(
        PropTypes.shape({
            gridId: PropTypes.string,
            gridSort: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.string,
                desc: PropTypes.bool
            }))
        })
    ),
    editMapping: PropTypes.func.isRequired,
    equityCreditLoading: PropTypes.bool,
    equityCreditRows: PropTypes.arrayOf(PropTypes.shape({})),
    equityMappingLoading: PropTypes.bool,
    equityMappingRows: PropTypes.arrayOf(PropTypes.shape({})),
    fetchDropdownOptions: PropTypes.func.isRequired,
    isdaMappingDropdownOptions: PropTypes.arrayOf(PropTypes.string),
    isdaLoading: PropTypes.bool,
    isdaRows: PropTypes.arrayOf(PropTypes.shape({})),
    staticDataMappingsLoaded: PropTypes.func.isRequired,
    setGridSorts: PropTypes.func.isRequired,
    styles: PropTypes.objectOf(PropTypes.string),
    standardProductClassLoading: PropTypes.bool,
    standardProductClassRows: PropTypes.arrayOf(PropTypes.shape({})),
    standardProductClassDropdown: PropTypes.arrayOf(PropTypes.string),
    currencyMappingLoading: PropTypes.bool,
    currencyMappingRows: PropTypes.arrayOf(PropTypes.shape({})),
    currencyMappingDropdown: PropTypes.arrayOf(PropTypes.string)
};

StaticDataMappings.defaultProps = {
    commodityLoading: false,
    commodityMappingDropdownOptions: [],
    commodityRows: [],
    defaultSort: [],
    equityCreditLoading: false,
    equityCreditRows: [],
    equityMappingLoading: false,
    equityMappingRows: [],
    isdaLoading: false,
    isdaMappingDropdownOptions: [],
    isdaRows: [],
    styles: defaultStyles,
    standardProductClassLoading: false,
    standardProductClassRows: [],
    standardProductClassDropdown: [],
    currencyMappingLoading: false,
    currencyMappingRows: [],
    currencyMappingDropdown: []
};

const mapStateToProps = state => ({
    commodityLoading: getCommodityLoading(state),
    commodityMappingDropdownOptions: getCommodityMappingDropdownOptions(state),
    commodityRows: getCommodityRows(state),
    defaultSort: getDefaultSort(state),
    equityCreditLoading: getEquityCreditLoading(state),
    equityCreditRows: getEquityCreditRows(state),
    equityMappingLoading: getEquityMappingLoading(state),
    equityMappingRows: getEquityMappingRows(state),
    isdaLoading: getIsdaLoading(state),
    isdaMappingDropdownOptions: getIsdaMappingDropdownOptions(state),
    isdaRows: getIsdaRows(state),
    standardProductClassLoading: getStandardProductClassLoading(state),
    standardProductClassRows: getStandardProductClassRows(state),
    standardProductClassDropdown: getStandardProductClassDropdown(state),
    currencyMappingRows: getCurrencyMappingRows(state),
    currencyMappingLoading: getCurrencyMappingLoading(state),
    currencyMappingDropdown: getCurrencyDropdownOptions(state),
});

const mapDispatchToProps = dispatch => ({
    createNewMapping: mapping => (...args) => dispatch(createNewMapping(mapping)(...args)),
    deleteMapping: mapping => (...args) => dispatch(deleteMapping(mapping)(...args)),
    editMapping: mapping => (...args) => dispatch(editMapping(mapping)(...args)),
    fetchDropdownOptions: mapping => (...args) => dispatch(fetchDropdownOptions(mapping)(...args)),
    staticDataMappingsLoaded: (...args) => dispatch(staticDataMappingsLoaded(...args)),
    setGridSorts: (...args) => dispatch(setGridSorts(...args))
});

export default withRouter(connect(mapStateToProps,
    mapDispatchToProps)(StaticDataMappings));

export { StaticDataMappings as StaticDataMappingsUnconnected };
